// SEO and Content Management for Instructor Signup Page

export const instructorSignupSEO = {
  pageTitle: 'Become a Yoga Instructor | MetaFit - Register Now',
  pageDescription: 'Join MetaFit as a certified yoga instructor. Complete your registration in 3 simple steps. Get access to thousands of students and grow your wellness business.',
  canonicalUrl: 'https://metafit.com/instructor/signup',
  
  // Open Graph Tags
  og: {
    title: 'Become a Yoga Instructor | MetaFit',
    description: 'Join MetaFit as a certified yoga instructor. Reach students globally and grow your wellness teaching career.',
    image: 'https://metafit.com/og-instructor-signup.jpg',
    type: 'website',
  },

  // Schema.org Structured Data
  structuredData: {
    '@context': 'https://schema.org',
    '@type': 'RegistrationForm',
    'name': 'Yoga Instructor Registration',
    'description': 'Complete your profile as a certified yoga instructor on MetaFit',
    'url': 'https://metafit.com/instructor/signup',
    'applicationCategory': 'BusinessApplication',
  },

  // Keywords
  keywords: [
    'yoga instructor registration',
    'yoga teacher signup',
    'become a yoga instructor',
    'online yoga teaching',
    'teach yoga online',
    'yoga instructor profile',
    'certified yoga teacher',
    'yoga business',
    'treatment and retreat instructor',
    'meditation teacher',
  ],
};

export const heroSectionContent = {
  title: 'Share Your Wellness Expertise',
  subtitle: 'Join thousands of instructors teaching on MetaFit',
  description: 'Reach eager students worldwide and grow your wellness teaching career with our comprehensive platform.',
  benefits: [
    {
      icon: '👥',
      title: 'Access to Students',
      description: 'Connect with thousands of students actively looking for quality instruction',
    },
    {
      icon: '💰',
      title: 'Flexible Pricing',
      description: 'Set your own rates and earn directly from your classes',
    },
    {
      icon: '📱',
      title: 'Easy to Use',
      description: 'Simple interface to manage classes and track your growth',
    },
    {
      icon: '🌍',
      title: 'Global Reach',
      description: 'Teach students from all over the world in your own timezone',
    },
    {
      icon: '📊',
      title: 'Analytics & Insights',
      description: 'Track student progress and get detailed performance metrics',
    },
    {
      icon: '✨',
      title: 'Professional Tools',
      description: 'Access scheduling, payment, and student management tools',
    },
  ],
};

export const loginPanelContent = {
  heading: 'Already Have an Account?',
  subheading: 'Sign in to access your instructor dashboard',
  description: 'If you already have an account with us, you can sign in here to access your profile and manage your classes.',
  buttonText: 'Go to Login',
  benefits: [
    'Access your instructor dashboard',
    'View your class schedule',
    'Manage student enrollments',
    'Track earnings and analytics',
    'Update your profile',
  ],
  features: [
    {
      title: 'Secure Login',
      description: 'Your account is protected with enterprise-grade security',
    },
    {
      title: 'Quick Access',
      description: 'Login takes less than a minute',
    },
    {
      title: 'Multi-Device Support',
      description: 'Access your account from any device',
    },
  ],
};

export const formSteps = {
  step1: {
    title: 'Personal Information',
    description: 'Tell us about yourself',
    fields: [
      {
        name: 'firstName',
        label: 'First Name',
        placeholder: 'Enter your first name',
        required: true,
      },
      {
        name: 'lastName',
        label: 'Last Name',
        placeholder: 'Enter your last name',
        required: true,
      },
      {
        name: 'email',
        label: 'Email Address',
        placeholder: 'Enter your email',
        type: 'email',
        required: true,
        help: 'We will send you a verification link',
      },
      {
        name: 'phone',
        label: 'Phone Number',
        placeholder: 'Enter 10-digit phone number',
        type: 'tel',
        required: true,
        help: 'We\'ll send you an OTP for verification',
      },
      {
        name: 'role',
        label: 'Instructor Type',
        placeholder: 'Select your specialization',
        type: 'select',
        required: true,
        options: [
          'Yoga Instructor',
          'Mental Wellness',
          'Ayurvedic Doctor',
          'Gym & Personal Trainer',
          'Treatment and Retreat',
        ],
      },
      {
        name: 'password',
        label: 'Password',
        placeholder: 'Create a strong password',
        type: 'password',
        required: true,
        help: 'At least 8 characters with uppercase, lowercase, and numbers',
      },
      {
        name: 'confirmPassword',
        label: 'Confirm Password',
        placeholder: 'Re-enter your password',
        type: 'password',
        required: true,
      },
    ],
  },
  step2: {
    title: 'Verify Your Phone',
    description: 'Complete phone verification with OTP',
    help: 'We sent a 6-digit code to your phone number',
  },
  step3: {
    title: 'Bank & Certification',
    description: 'Add payment details and upload certifications',
    sections: [
      {
        title: 'Bank Details (Optional)',
        description: 'Required for receiving payments',
        fields: [
          {
            name: 'bankName',
            label: 'Bank Name',
            placeholder: 'e.g., HDFC Bank',
            help: 'Letters and hyphens only',
          },
          {
            name: 'accountNumber',
            label: 'Account Number',
            placeholder: '9-18 digits',
            help: 'Digits only, no spaces',
          },
          {
            name: 'ifscCode',
            label: 'IFSC Code',
            placeholder: 'e.g., HDFC0000985',
            help: 'Format: 4 letters + 0 + 6 alphanumeric',
          },
        ],
      },
      {
        title: 'Certification',
        description: 'Upload your yoga teaching certificate',
        fileTypes: ['PDF', 'JPG', 'PNG'],
        maxSize: '5MB',
      },
      {
        title: 'Agreements',
        description: 'Please agree to our terms to complete registration',
        agreements: [
          {
            id: 'agreeToTerms',
            text: 'I agree to the Terms & Privacy Policy',
            required: true,
          },
          {
            id: 'subscribeNewsletter',
            text: 'Subscribe me to MetaFit newsletter for updates and tips',
            required: false,
          },
        ],
      },
    ],
  },
};

export const faqContent = [
  {
    question: 'What qualifications do I need to become an instructor?',
    answer: 'You should have a valid yoga teaching certification from a recognized institution. MetaFit verifies all instructor credentials.',
  },
  {
    question: 'How long does the approval process take?',
    answer: 'Typically 2-3 business days. Our team reviews your credentials and certification documents.',
  },
  {
    question: 'When can I start teaching?',
    answer: 'Once your account is approved by our team, you can immediately create and publish your classes.',
  },
  {
    question: 'How do I set my class prices?',
    answer: 'You have full control over pricing. Set per-session, monthly, or yearly rates. MetaFit handles payments securely.',
  },
  {
    question: 'What commission does MetaFit take?',
    answer: 'MetaFit takes a small commission on each class enrollment. Details are provided during registration.',
  },
  {
    question: 'Can I teach in multiple time zones?',
    answer: 'Yes! You can schedule classes for different time zones and reach students globally.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We support bank transfers, UPI, and other digital payment methods for instructor payouts.',
  },
  {
    question: 'Is my bank information secure?',
    answer: 'Yes, all financial information is encrypted and stored securely according to industry standards.',
  },
];

export const testimonials = [
  {
    name: 'Priya Sharma',
    role: 'Yoga Instructor, Delhi',
    image: 'https://metafit.com/instructor-1.jpg',
    text: 'MetaFit helped me reach students globally. I went from teaching 5 students to 50 in just 3 months!',
    rating: 5,
  },
  {
    name: 'Rahul Patel',
    role: 'Wellness Coach, Mumbai',
    image: 'https://metafit.com/instructor-2.jpg',
    text: 'The platform is incredibly user-friendly. Setting up my classes took less than an hour.',
    rating: 5,
  },
  {
    name: 'Anjali Singh',
    role: 'Meditation Teacher, Bangalore',
    image: 'https://metafit.com/instructor-3.jpg',
    text: 'Great payment system and student support. Highly recommend MetaFit for all instructors!',
    rating: 5,
  },
];

export const successMessages = {
  registrationSuccess: 'Welcome to MetaFit! Your account has been created successfully.',
  phoneVerified: 'Phone number verified successfully!',
  bankDetailsAdded: 'Bank details saved successfully.',
  certificationUploaded: 'Certification uploaded successfully.',
};

export const errorMessages = {
  invalidEmail: 'Please enter a valid email address',
  passwordTooWeak: 'Password must be at least 8 characters with uppercase, lowercase, and numbers',
  passwordsMismatch: 'Passwords do not match',
  invalidPhone: 'Please enter a valid 10-digit phone number',
  certificateRequired: 'Please upload your teaching certificate',
  invalidBankDetails: 'Please check your bank details format',
  termsRequired: 'You must agree to the terms to continue',
};

// Trust & Stats Section
export const trustSection = {
  title: 'Why Choose MetaFit?',
  description: 'Join a trusted platform trusted by wellness professionals across India',
  stats: [
    {
      icon: '👥',
      number: '50K+',
      label: 'Active Students',
      description: 'Learning with our instructors daily',
    },
    {
      icon: '🎓',
      number: '2000+',
      label: 'Verified Instructors',
      description: 'Certified and experienced professionals',
    },
    {
      icon: '📚',
      number: '5001+',
      label: 'Classes',
      description: 'Diverse treatment and retreat content',
    },
    {
      icon: '⭐',
      number: '4.8/5',
      label: 'Average Rating',
      description: 'From student reviews',
    },
  ],
};

// How It Works Section
export const howItWorks = {
  title: 'How MetaFit Works for Instructors',
  description: 'Get started in just a few simple steps',
  steps: [
    {
      number: '01',
      title: 'Register Your Profile',
      description: 'Create your account with your credentials and teaching qualifications',
      icon: '📝',
      time: '5 minutes',
    },
    {
      number: '02',
      title: 'Get Verified',
      description: 'Our team verifies your credentials and approves your account',
      icon: '✅',
      time: '2-3 days',
    },
    {
      number: '03',
      title: 'Create Your Classes',
      description: 'Design and schedule classes that match your expertise and availability',
      icon: '🎯',
      time: '15 minutes',
    },
    {
      number: '04',
      title: 'Start Teaching',
      description: 'Begin earning and connect with students from around the world',
      icon: '🚀',
      time: 'Immediate',
    },
  ],
};

// Expanded FAQ
export const expandedFAQ = [
  {
    category: 'Getting Started',
    faqs: [
      {
        question: 'What qualifications do I need?',
        answer: 'You should have a valid yoga or treatment and retreat teaching certification from a recognized institution. We accept certificates from organizations like YAI, AAYUSH-recognized institutes, and international yoga alliances.',
      },
      {
        question: 'How much does it cost to join MetaFit?',
        answer: 'Registration is completely FREE! We only take a commission on classes you actually teach, so you only pay when you earn.',
      },
      {
        question: 'How long is the approval process?',
        answer: 'Usually 2-3 business days. We review your credentials, certification documents, and background. You\'ll get an email with the status.',
      },
    ],
  },
  {
    category: 'Teaching & Classes',
    faqs: [
      {
        question: 'What types of classes can I create?',
        answer: 'You can create live classes, pre-recorded sessions, workshops, and courses. Choose from Yoga, Meditation, Fitness, Treatment and Retreat Coaching, Ayurveda, and more.',
      },
      {
        question: 'Can I set my own pricing?',
        answer: 'Absolutely! You have full control over pricing. Set per-class rates, monthly subscriptions, or yearly memberships. MetaFit handles all transactions securely.',
      },
      {
        question: 'What class sizes can I teach?',
        answer: 'From 1-on-1 personalized sessions to group classes of 100+. Our platform scales with your needs.',
      },
      {
        question: 'Can I teach in my timezone?',
        answer: 'Yes! Schedule classes in your timezone, and MetaFit automatically converts to student timezones. You can reach global students without changing your schedule.',
      },
    ],
  },
  
  {
    category: 'Support & Security',
    faqs: [
      {
        question: 'Is my bank information secure?',
        answer: 'Yes! All data is encrypted using SSL technology and complies with PCI DSS standards. Your financial information is never shared with anyone.',
      },
      {
        question: 'What if I need help?',
        answer: 'Our support team is available 24/7 via chat, email, and phone. We also provide detailed documentation and video tutorials.',
      },
      {
        question: 'Can I delete my account?',
        answer: 'Yes, you can delete your account anytime. However, students can still access classes they\'ve already enrolled in.',
      },
    ],
  },
];

// Instructor Testimonials with Details
export const detailedTestimonials = [
  {
    name: 'Priya Sharma',
    role: 'Yoga Instructor',
    location: 'Delhi',
    image: '👩‍🏫',
    text: 'MetaFit completely transformed my teaching career. I went from teaching 5 students locally to 50+ students globally in just 3 months. The platform is intuitive and the support team is always helpful!',
    rating: 5,
    earnings: '₹1,00,000+',
    students: '50+',
    classes: '150+',
  },
  {
    name: 'Rahul Patel',
    role: 'Fitness Coach',
    location: 'Mumbai',
    image: '👨‍💼',
    text: 'The best part is how easy it is to manage everything. Creating classes, scheduling, tracking students - it all takes minutes. I spend more time teaching and less time on admin work.',
    rating: 5,
    earnings: '₹80,000+',
    students: '40+',
    classes: '120+',
  },
  {
    name: 'Anjali Singh',
    role: 'Meditation Teacher',
    location: 'Bangalore',
    image: '🧘‍♀️',
    text: 'The payment system is super reliable and transparent. I know exactly how much I\'m earning, when it will be deposited, and there are no hidden charges. Highly recommend!',
    rating: 5,
    earnings: '₹1,20,000+',
    students: '60+',
    classes: '180+',
  },
  {
    name: 'Vikram Kumar',
    role: 'Ayurveda Consultant',
    location: 'Pune',
    image: '👨‍⚕️',
    text: 'The tools provided are professional-grade. Analytics, scheduling, student management - everything is well-designed. My students love the platform too!',
    rating: 5,
    earnings: '₹95,000+',
    students: '35+',
    classes: '100+',
  },
];

// Features Section
export const featuresSection = {
  title: 'Powerful Tools for Instructors',
  description: 'Everything you need to teach, manage, and grow',
  features: [
    {
      icon: '📅',
      title: 'Smart Scheduling',
      description: 'Manage classes, time zones, and student availability with ease. Set recurring schedules and handle one-on-one bookings.',
    },
    {
      icon: '💳',
      title: 'Secure Payments',
      description: 'Automatic payment processing with no hidden fees. Track earnings in real-time with detailed analytics.',
    },
    {
      icon: '📊',
      title: 'Analytics Dashboard',
      description: 'Monitor student engagement, class performance, and earnings trends with detailed reports.',
    },
    {
      icon: '📱',
      title: 'Mobile App',
      description: 'Teach and manage classes on the go with our intuitive mobile application.',
    },
    {
      icon: '🎥',
      title: 'Video Streaming',
      description: 'High-quality video streaming with screen sharing for live and recorded sessions.',
    },
    {
      icon: '👥',
      title: 'Student Management',
      description: 'Manage enrollments, send announcements, and track student progress effortlessly.',
    },
    {
      icon: '🔐',
      title: 'Data Security',
      description: 'Enterprise-grade encryption and compliance with international data protection standards.',
    },
    {
      icon: '📞',
      title: '24/7 Support',
      description: 'Dedicated support team ready to help you via chat, email, and phone.',
    },
  ],
};

// CTA Section
export const ctaSection = {
  title: 'Ready to Share Your Expertise?',
  subtitle: 'Join thousands of instructors earning on MetaFit',
  description: 'Start your journey today. Registration takes just 5 minutes and costs nothing!',
  trustBadges: [
    '✅ Trusted by 50,000+ students',
    '🔒 100% secure & encrypted',
    '⚡ Instant account setup',
    '💰 No hidden fees',
  ],
};

// Footer Content
export const footerContent = {
  quickLinks: [
    { title: 'Home', url: '/' },
    { title: 'About Us', url: '/about' },
    { title: 'Contact', url: '/contact' },
    { title: 'Blog', url: '/blog' },
  ],
  legalLinks: [
    { title: 'Terms of Service', url: '/terms' },
    { title: 'Privacy Policy', url: '/privacy' },
    { title: 'Cookie Policy', url: '/cookies' },
  ],
  socialLinks: [
    { platform: 'Facebook', url: 'https://facebook.com/metafit' },
    { platform: 'Instagram', url: 'https://instagram.com/metafit' },
    { platform: 'Twitter', url: 'https://twitter.com/metafit' },
    { platform: 'LinkedIn', url: 'https://linkedin.com/company/metafit' },
  ],
  contact: {
    email: 'instructors@metafit.com',
    phone: '+91-XXXXXXXXXX',
    address: 'MetaFit, India',
  },
};
